/**
 * Super Admin Brands — list + Add Brand modal (atomic brand+owner create).
 *
 * Additions in the subscription rollout:
 *   - PLAN column         — current subscription window (30/90/180/360 d)
 *   - EXPIRES IN column   — `{n} days left`, amber pill ≤7 d, red pill 0
 *   - Extend action       — bump expiry by a chosen plan length
 *   - Delete action       — soft-delete with "type brand name to confirm"
 *   - NewBrandModal: Subscription Duration dropdown (defaults to 30)
 * Nothing else in this file changes (atomic create flow, columns,
 * test IDs, table layout, image uploaders, color picker — all the same).
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Building2, Power, ArrowRight, Trash2, Clock, Tags, Pencil, Crown, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import Modal from '@/components/ui_primitives/Modal';
import { FieldRow, Input, Textarea, ColorField } from '@/components/ui_primitives/Field';
import ImageUploader from '@/components/ui_primitives/ImageUploader';
import { confirm } from '@/components/ui_primitives/confirm';
import { CategoryChipPreview } from '@/pages/admin/CategoriesPage';

// Subscription plan options shared by the create modal and the
// per-row Extend modal. Keep in lockstep with the backend's
// ALLOWED_SUBSCRIPTION_DAYS — both ends validate.
const PLAN_OPTIONS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '180 days' },
  { days: 365, label: '365 days (1 year)' },
];
const DEFAULT_PLAN_DAYS = 365;

const schema = z.object({
  name: z.string().min(1).max(120),
  slug: z.string().max(120).optional().or(z.literal('')),
  description: z.string().min(1).max(1000),
  primary_color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use #RRGGBB hex'),
  stamp_threshold_rupiah: z.coerce.number().int().min(1).max(10_000_000),
  stamps_per_card: z.coerce.number().int().min(2).max(50).default(10),
  subscription_days: z.coerce.number().int().refine(
    (v) => PLAN_OPTIONS.some((p) => p.days === v),
    'Pick 30 / 90 / 180 / 360',
  ),
  owner: z.object({
    name: z.string().min(1).max(120),
    email: z.string().email(),
    password: z.string().min(6).max(120),
  }),
});

// Pure helper — render the "Expires in" cell from the server-side
// `expires_in_days` integer. Returns a tuple [pill?, text?] so the
// table cell can decide layout. Kept outside the component so it has
// no dependency on hooks state and is trivial to scan.
function renderExpires(expiresInDays) {
  if (expiresInDays === null || expiresInDays === undefined) {
    return { pill: null, text: <span className="text-white/30">—</span> };
  }
  if (expiresInDays <= 0) {
    return {
      pill: <StatusPill variant="red">Expired</StatusPill>,
      text: null,
    };
  }
  if (expiresInDays <= 7) {
    return {
      pill: <StatusPill variant="amber">Expiring soon</StatusPill>,
      text: (
        <span className="text-[11px] text-white/50">
          {expiresInDays} day{expiresInDays === 1 ? '' : 's'} left
        </span>
      ),
    };
  }
  return {
    pill: null,
    text: (
      <span className="text-sm text-white/70">{expiresInDays} days left</span>
    ),
  };
}

export default function AdminBrandsPage() {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const [openNew, setOpenNew] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // brand object
  const [extendTarget, setExtendTarget] = useState(null); // brand object
  const [categoriesTarget, setCategoriesTarget] = useState(null); // brand

  const brandsQ = useQuery({
    queryKey: qk.adminBrands(),
    queryFn: () => api.adminBrands(token),
    enabled: !!token,
  });
  // Phase W-3 — categories lookup for the inline chips column + the
  // edit modal. Cached at page level so the table doesn't fan out one
  // request per row.
  const categoriesQ = useQuery({
    queryKey: qk.categories(),
    queryFn: () => api.categories(token),
    enabled: !!token,
  });
  const catById = new Map((categoriesQ.data ?? []).map((c) => [c.id, c]));

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.adminBrands() });
  const toggle = useMutation({
    mutationFn: ({ id, on }) =>
      on ? api.adminActivateBrand(token, id) : api.adminDeactivateBrand(token, id),
    onSuccess: (_d, v) => { toast.success(v.on ? 'Brand activated' : 'Brand deactivated'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Action failed')),
  });
  // Phase SA_PRO_TOGGLE_UI 2026-06-19 — SA flips Brand.pro_enabled via
  // existing PATCH /api/admin/brands/{id}/pro (audit-emitting).
  const proToggle = useMutation({
    mutationFn: ({ id, pro_enabled, reason }) =>
      api.adminToggleBrandPro(token, id, pro_enabled, reason),
    onSuccess: (_d, v) => {
      toast.success(v.pro_enabled ? 'Switched to OneStamp Pro' : 'Switched to OneStamp Basic');
      invalidate();
    },
    onError: (e) => toast.error(String(e?.detail ?? 'Plan switch failed')),
  });

  if (brandsQ.isLoading) return <FullSpinner />;
  if (brandsQ.error) return <QueryError error={brandsQ.error} onRetry={() => brandsQ.refetch()} />;
  const items = brandsQ.data || [];

  return (
    <div className="space-y-6" data-testid="admin-brands-page">
      <PageHeader
        kicker="Brands"
        title="All brands"
        subtitle="Onboard a new brand by creating a brand record and its owner account in one step."
        actions={<Button leftIcon={<Plus size={14} />} onClick={() => setOpenNew(true)} testID="brand-add">Add brand</Button>}
      />

      {items.length === 0 ? (
        <EmptyState icon={<Building2 size={20} />} title="No brands yet" subtitle="Bring your first brand onto the platform."
          action={<Button leftIcon={<Plus size={14} />} onClick={() => setOpenNew(true)}>Add brand</Button>} />
      ) : (
        <SectionCard padded={false}>
          <Table testID="admin-brands-table">
            <THead>
              <Tr>
                <Th>Brand</Th>
                <Th>Owner</Th>
                <Th>Branches</Th>
                <Th>Active customers</Th>
                <Th>Plan</Th>
                <Th>Categories</Th>
                <Th>Expires in</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </Tr>
            </THead>
            <TBody>
              {items.map((b) => {
                const expires = renderExpires(b.expires_in_days);
                return (
                <Tr key={b.id}>
                  <Td>
                    <div className="flex items-center gap-3">
                      {b.logo_url ? (
                        <img src={resolveImageUrl(b.logo_url)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 grid place-items-center text-white/30"><Building2 size={14} /></div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{b.name}</div>
                        <div className="text-[11px] text-white/40 font-mono">{b.slug}</div>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    {b.owner ? (
                      <div>
                        <div className="text-sm">{b.owner.name}</div>
                        <div className="text-[11px] text-white/40 font-mono">{b.owner.email}</div>
                      </div>
                    ) : <span className="text-white/30">— no owner —</span>}
                  </Td>
                  <Td>{b.branch_count ?? 0}</Td>
                  <Td>{(b.active_customers ?? 0).toLocaleString('id-ID')}</Td>
                  <Td>
                    <div
                      className="inline-flex items-center gap-2"
                      data-testid={`brand-plan-${b.id}`}
                    >
                      <StatusPill
                        variant={b.pro_enabled ? 'gold' : 'slate'}
                        testID={`brand-plan-tier-${b.id}`}
                      >
                        {b.pro_enabled ? 'OneStamp Pro' : 'OneStamp Basic'}
                      </StatusPill>
                      {b.subscription_days ? (
                        <span className="text-[11px] font-mono text-white/70 px-2 py-0.5 rounded-md bg-white/5 border border-white/10">
                          {b.subscription_days}d
                        </span>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <BrandCategoryCell
                      brand={b}
                      catById={catById}
                      onEdit={() => setCategoriesTarget(b)}
                    />
                  </Td>
                  <Td>
                    <div
                      className="flex items-center gap-2"
                      data-testid={`brand-expires-${b.id}`}
                    >
                      {expires.pill}
                      {expires.text}
                    </div>
                  </Td>
                  <Td>{b.is_active ? <StatusPill variant="gold">Active</StatusPill> : <StatusPill variant="slate">Inactive</StatusPill>}</Td>
                  <Td align="right">
                    <div className="inline-flex gap-1.5">
                      <Link to={`/admin/brands/${b.id}`}>
                        <Button size="sm" variant="subtle" rightIcon={<ArrowRight size={12} />} testID={`brand-open-${b.id}`}>Drill in</Button>
                      </Link>
                      <Button size="sm" variant="subtle" leftIcon={<Clock size={12} />}
                        onClick={() => setExtendTarget(b)}
                        testID={`brand-extend-${b.id}`}>
                        Extend
                      </Button>
                      <Button size="sm" variant="subtle"
                        leftIcon={b.pro_enabled ? <Tag size={12} /> : <Crown size={12} />}
                        onClick={async () => {
                          const toPro = !b.pro_enabled;
                          const modules = 'Reservations · Floor Plan · Waitlist · CRM · Staff · Promotions · Review Links · Analytics';
                          const ok = await confirm({
                            title: toPro ? `Switch ${b.name} to OneStamp Pro?` : `Switch ${b.name} to OneStamp Basic?`,
                            message: toPro
                              ? `Switching ${b.name} to OneStamp Pro will show ${modules} for every staff user of this brand on next page load. Existing data is preserved; switch is reversible.`
                              : `Switching ${b.name} to OneStamp Basic will hide ${modules} for every staff user of this brand on next page load. Existing data is preserved; switch is reversible.`,
                            confirmText: toPro ? 'Switch to Pro' : 'Switch to Basic',
                            tone: 'primary',
                          });
                          if (ok) proToggle.mutate({ id: b.id, pro_enabled: toPro, reason: 'SA manual toggle from BrandsPage' });
                        }}
                        testID={`brand-pro-toggle-${b.id}`}>
                        {b.pro_enabled ? 'Switch to Basic' : 'Switch to Pro'}
                      </Button>
                      <Button size="sm" variant={b.is_active ? 'danger' : 'secondary'}
                        leftIcon={<Power size={12} />}
                        onClick={async () => {
                          const on = !b.is_active;
                          const ok = await confirm({
                            title: on ? `Activate ${b.name}?` : `Deactivate ${b.name}?`,
                            message: on ? 'Customers will see this brand again.' : 'Customers will no longer see this brand.',
                            confirmText: on ? 'Activate' : 'Deactivate',
                            tone: on ? 'primary' : 'danger',
                          });
                          if (ok) toggle.mutate({ id: b.id, on });
                        }}
                        testID={`brand-toggle-${b.id}`}>
                        {b.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button size="sm" variant="danger" leftIcon={<Trash2 size={12} />}
                        onClick={() => setDeleteTarget(b)}
                        testID={`brand-delete-${b.id}`}>
                        Delete
                      </Button>
                    </div>
                  </Td>
                </Tr>
              );})}
            </TBody>
          </Table>
        </SectionCard>
      )}

      {openNew ? (
        <NewBrandModal onClose={() => setOpenNew(false)} onSaved={() => { invalidate(); setOpenNew(false); }} />
      ) : null}
      {deleteTarget ? (
        <DeleteBrandModal
          brand={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { invalidate(); setDeleteTarget(null); }}
        />
      ) : null}
      {extendTarget ? (
        <ExtendBrandModal
          brand={extendTarget}
          onClose={() => setExtendTarget(null)}
          onExtended={() => { invalidate(); setExtendTarget(null); }}
        />
      ) : null}
      {categoriesTarget ? (
        <BrandCategoriesEditModal
          brand={categoriesTarget}
          categories={categoriesQ.data ?? []}
          onClose={() => setCategoriesTarget(null)}
          onSaved={() => { invalidate(); setCategoriesTarget(null); }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase W-3 — categories cell + inline edit modal.
// W-3 follow-up: surfaces an obvious edit affordance.
//   • Brands WITH categories: chip pills + a visible Pencil icon button
//     that's always rendered (no longer hover-only) and shares the same
//     click handler as the cell.
//   • Brands with empty category_ids[]: the dead-end italic
//     "Uncategorized" label is replaced with a prominent amber
//     dashed-border CTA "+ Assign categories" that opens the same
//     edit modal. (Folded Fix F into this branch.)
// ---------------------------------------------------------------------------
function BrandCategoryCell({ brand, catById, onEdit }) {
  const ids = brand.category_ids ?? [];
  const resolved = ids.map((id) => catById.get(id)).filter(Boolean);
  const visible = resolved.slice(0, 3);
  const more = resolved.length - visible.length;

  // ------- Empty-array branch — amber dashed CTA --------------------------
  if (visible.length === 0) {
    return (
      <button
        type="button"
        onClick={onEdit}
        data-testid={`admin-brand-assign-categories-${brand.id}`}
        title="Click to assign categories"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-dashed border-amber-400/55 bg-amber-500/[0.06] text-amber-200 text-[11px] font-medium hover:bg-amber-500/15 hover:border-amber-400 transition-colors"
      >
        <Plus size={12} /> Assign categories
      </button>
    );
  }

  // ------- Populated branch — chips + visible pencil ----------------------
  return (
    <button
      type="button"
      onClick={onEdit}
      data-testid={`admin-brand-categories-cell-${brand.id}`}
      title="Click to edit categories"
      className="group inline-flex items-center gap-1.5 flex-wrap text-left rounded-lg px-1 py-0.5 -mx-1 -my-0.5 hover:bg-white/[0.04] hover:ring-1 hover:ring-gold/30 transition-all"
    >
      {visible.map((c) => (
        <CategoryChipPreview key={c.id} cat={c} compact />
      ))}
      {more > 0 ? (
        <span className="text-[10px] font-mono text-white/45 px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10">
          +{more}
        </span>
      ) : null}
      <span
        data-testid={`admin-brand-categories-edit-${brand.id}`}
        className="inline-flex items-center justify-center w-5 h-5 rounded-md border border-white/10 bg-white/5 text-white/45 group-hover:text-gold group-hover:border-gold/50 group-hover:bg-gold/10 transition-colors"
        aria-label="Edit categories"
      >
        <Pencil size={11} />
      </span>
    </button>
  );
}

function BrandCategoriesEditModal({ brand, categories, onClose, onSaved }) {
  const token = useAuthStore((s) => s.token);
  const [selected, setSelected] = useState(brand.category_ids ?? []);
  const [busy, setBusy] = useState(false);

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const onSubmit = async () => {
    setBusy(true);
    try {
      await api.adminPatchBrand(token, brand.id, { category_ids: selected });
      toast.success('Categories updated');
      onSaved();
    } catch (e) {
      const code = e?.detail?.code;
      toast.error(
        code === 'invalid_category_id'
          ? 'One or more categories are no longer valid.'
          : String(e?.detail?.message ?? e?.detail ?? 'Save failed'),
      );
      setBusy(false);
    }
  };

  // Only show active+non-deleted in the picker — the public
  // /api/categories already filters but be defensive.
  const options = categories
    .filter((c) => c.is_active && !c.deleted_at)
    .sort((a, b) => (a.sort_index - b.sort_index) || a.name.localeCompare(b.name));

  return (
    <Modal
      open
      onClose={onClose}
      title={`Categories for ${brand.name}`}
      size="md"
      subtitle="Select one or more. Customers can filter brands by category. Leave all unchecked = automatically tagged 'Other'."
      testID="admin-brand-categories-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} loading={busy} testID="admin-brand-categories-save">
            Save
          </Button>
        </>
      }
    >
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-2"
        data-testid="admin-brand-categories-grid"
      >
        {options.map((c) => {
          const on = selected.includes(c.id);
          return (
            <label
              key={c.id}
              data-testid={`admin-brand-categories-option-${c.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors text-sm ${
                on
                  ? 'bg-gold/10 border-gold/45 text-white'
                  : 'bg-[#1A1A1A] border-white/8 text-white/80 hover:border-white/20'
              }`}
            >
              <input
                type="checkbox"
                className="accent-gold w-4 h-4"
                checked={on}
                onChange={() => toggle(c.id)}
              />
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: c.color || '#9CA3AF' }}
              />
              <span className="truncate">{c.name}</span>
              <span className="ml-auto font-mono text-[10px] text-white/35">{c.slug}</span>
            </label>
          );
        })}
      </div>
    </Modal>
  );
}

function NewBrandModal({ onClose, onSaved }) {
  const token = useAuthStore((s) => s.token);
  const [logo, setLogo] = useState('');
  const [banner, setBanner] = useState('');
  // W-3 follow-up — pick categories at create time. Empty = backend
  // auto-fills to ["Other"], which matches the rest of the system.
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const categoriesQ = useQuery({
    queryKey: qk.categories(),
    queryFn: () => api.categories(token),
    enabled: !!token,
  });
  const toggleCategory = (id) =>
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '', slug: '', description: '', primary_color: '#C9A84C',
      stamp_threshold_rupiah: 50_000, stamps_per_card: 10,
      subscription_days: DEFAULT_PLAN_DAYS,
      owner: { name: '', email: '', password: '' },
    },
  });
  const color = watch('primary_color');

  const onSubmit = async (vals) => {
    try {
      const body = {
        ...vals,
        slug: vals.slug || undefined,
        logo_url: logo || undefined,
        banner_url: banner || undefined,
        // W-3 follow-up — include selected category ids when non-empty.
        // Backend will auto-fill `["Other"]` if we omit / send empty.
        category_ids: selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
      };
      await api.adminCreateBrand(token, body);
      toast.success(`Brand "${vals.name}" created`);
      onSaved();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Create failed'));
    }
  };

  return (
    <Modal open onClose={onClose} title="Add brand" size="lg"
      subtitle="Creates the brand and its primary owner account atomically."
      testID="brand-new-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting} testID="brand-new-save">Create brand</Button>
        </>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Logo (square)"><ImageUploader value={logo} onChange={setLogo} aspect="square" /></FieldRow>
          <FieldRow label="Banner (3:1)"><ImageUploader value={banner} onChange={setBanner} aspect="banner" /></FieldRow>
        </div>
        <FieldRow label="Brand name" required error={errors.name?.message}>
          <Input {...register('name')} placeholder="Salemba 43, Kopi ABC, …" />
        </FieldRow>
        <FieldRow label="Slug" hint="Leave blank to auto-generate from name" error={errors.slug?.message}>
          <Input {...register('slug')} placeholder="salemba-43" />
        </FieldRow>
        <FieldRow label="Description" required error={errors.description?.message}>
          <Textarea rows={3} {...register('description')} />
        </FieldRow>
        {/* W-3 follow-up — Categories multi-select. Default empty;
            backend auto-fills "Other" when nothing is selected. */}
        <FieldRow
          label="Categories"
          hint="Customers can filter brands by category on the All Brands page."
        >
          {categoriesQ.isLoading ? (
            <div className="h-10 rounded-lg bg-white/[0.03] border border-white/5 animate-pulse" />
          ) : (
            <>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 gap-1.5"
                data-testid="admin-new-brand-categories-grid"
              >
                {[...(categoriesQ.data ?? [])]
                  .filter((c) => c.is_active && !c.deleted_at)
                  .sort(
                    (a, b) =>
                      (a.sort_index - b.sort_index) || a.name.localeCompare(b.name),
                  )
                  .map((c) => {
                    const on = selectedCategoryIds.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        data-testid={`admin-new-brand-categories-option-${c.slug}`}
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors text-xs ${
                          on
                            ? 'bg-gold/10 border-gold/45 text-white'
                            : 'bg-[#1A1A1A] border-white/8 text-white/80 hover:border-white/20'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="accent-gold w-3.5 h-3.5"
                          checked={on}
                          onChange={() => toggleCategory(c.id)}
                        />
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: c.color || '#9CA3AF' }}
                        />
                        <span className="truncate">{c.name}</span>
                      </label>
                    );
                  })}
              </div>
              {selectedCategoryIds.length === 0 ? (
                <div
                  className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-400/35 text-amber-200 text-[11px]"
                  data-testid="admin-new-brand-categories-empty-note"
                >
                  <Tags size={12} />
                  No categories selected — this brand will be tagged{' '}
                  <strong>'Other'</strong> automatically.
                </div>
              ) : null}
            </>
          )}
        </FieldRow>
        <div className="grid grid-cols-3 gap-3">
          <FieldRow label="Primary color" required error={errors.primary_color?.message}>
            <ColorField value={color} {...register('primary_color')}
              onChange={(e) => setValue('primary_color', e.target.value, { shouldDirty: true })} />
          </FieldRow>
          <FieldRow label="Stamp threshold (Rp)" required error={errors.stamp_threshold_rupiah?.message}>
            <Input type="number" min={1} step={500} {...register('stamp_threshold_rupiah')} />
          </FieldRow>
          <FieldRow label="Stamps per card" required error={errors.stamps_per_card?.message}>
            <Input type="number" min={2} {...register('stamps_per_card')} />
          </FieldRow>
        </div>
        <FieldRow
          label="Subscription duration"
          required
          hint="Sets the initial billing window; auto-deactivates on expiry."
          error={errors.subscription_days?.message}
        >
          <select
            {...register('subscription_days')}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
            data-testid="brand-new-subscription"
          >
            {PLAN_OPTIONS.map((p) => (
              <option key={p.days} value={p.days}>{p.label}</option>
            ))}
          </select>
        </FieldRow>
        <div className="rounded-xl bg-black/30 border border-white/8 p-4 space-y-3">
          <div className="text-xs uppercase tracking-widest text-gold/80 font-semibold">Owner credentials</div>
          <FieldRow label="Owner name" required error={errors.owner?.name?.message}>
            <Input {...register('owner.name')} />
          </FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Owner email" required error={errors.owner?.email?.message}>
              <Input type="email" {...register('owner.email')} placeholder="owner@brand.com" />
            </FieldRow>
            <FieldRow label="Initial password" required error={errors.owner?.password?.message}>
              <Input type="password" {...register('owner.password')} placeholder="••••••••" />
            </FieldRow>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// Type-to-confirm delete modal. Matches the OwnersPage pattern so the
// admin sees the same drill (paste the brand name) wherever a hard
// action is staged.
function DeleteBrandModal({ brand, onClose, onDeleted }) {
  const token = useAuthStore((s) => s.token);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const matches = confirmText === brand.name;

  const onConfirm = async () => {
    if (!matches) return;
    setBusy(true);
    try {
      await api.adminDeleteBrand(token, brand.id, brand.name);
      toast.success(`"${brand.name}" deleted`);
      onDeleted();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Delete failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Delete ${brand.name}?`} size="md"
      subtitle="Cascades to the brand's owner, branches, and cashiers. Customer history is kept."
      testID="brand-delete-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!matches}
            loading={busy}
            onClick={onConfirm}
            testID="brand-delete-confirm"
          >
            Delete brand
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-white/70">
          This soft-deletes the brand and removes it from all customer
          and cashier surfaces. You can restore it later if needed.
        </p>
        <FieldRow
          label={
            <>
              Type <span className="font-mono text-gold">{brand.name}</span> to confirm
            </>
          }
        >
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={brand.name}
            autoFocus
            testID="brand-delete-confirm-input"
          />
        </FieldRow>
      </div>
    </Modal>
  );
}

// Extend modal — pick one of the four plan windows. Backend
// always extends from MAX(now, current_expiry), so picking 90 on a
// brand that still has 14 days left adds a full 90 from today, not
// 90 from the original expiry. Spec-correct: admin's click is treated
// as a fresh window.
function ExtendBrandModal({ brand, onClose, onExtended }) {
  const token = useAuthStore((s) => s.token);
  const [days, setDays] = useState(brand.subscription_days || 30);
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    setBusy(true);
    try {
      const res = await api.adminExtendBrand(token, brand.id, days);
      toast.success(`Extended by ${days} days · expires ${new Date(res.expiry_date).toLocaleDateString('id-ID')}`);
      onExtended();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Extend failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Extend ${brand.name}`} size="md"
      subtitle={
        brand.expiry_date
          ? `Current expiry: ${new Date(brand.expiry_date).toLocaleDateString('id-ID')}`
          : 'No prior expiry on record — sets a fresh window.'
      }
      testID="brand-extend-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            loading={busy}
            onClick={onSubmit}
            testID="brand-extend-confirm"
          >
            Extend by {days} days
          </Button>
        </>
      }
    >
      <FieldRow label="Subscription window" required>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
          data-testid="brand-extend-days"
        >
          {PLAN_OPTIONS.map((p) => (
            <option key={p.days} value={p.days}>{p.label}</option>
          ))}
        </select>
      </FieldRow>
    </Modal>
  );
}
